import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';

import { queryKeys } from '@/shared/api';
import { env } from '@/shared/config/env';
import { RealtimeClient, realtimeUrl, type ServerEvent } from '@/shared/realtime';

import { PresenceStore, RealtimeContext } from './context';
import {
  DATABASE_ACCESS_KEYS,
  FALLBACK_INTERVAL_MS,
  keysForCollection,
  keysForDatabase,
  RECORDS_MIN_INTERVAL_MS,
  shouldRefreshOnVisible,
} from './refresh';

/** Query keys refreshed when something changed on a board (a hint, never data). */
function keysFor(event: ServerEvent): readonly (readonly unknown[])[] {
  const boardId = event.board_id;
  if (!boardId) return [];
  const keys: (readonly unknown[])[] = [];
  switch (event.scope) {
    case 'comments':
      if (event.card_id) keys.push(queryKeys.collab.comments(event.card_id));
      if (event.card_id) keys.push(queryKeys.collab.watchers(event.card_id));
      break;
    case 'members':
      keys.push(queryKeys.collab.participants(boardId), queryKeys.collab.access(boardId));
      keys.push(queryKeys.collab.myBoards);
      break;
    default:
      keys.push(
        queryKeys.boards.detail(boardId),
        queryKeys.boards.searchAll(boardId),
        queryKeys.boards.timelineAll(boardId),
        queryKeys.calendar.all,
        queryKeys.activity.board(boardId),
      );
      if (event.card_id) {
        keys.push(queryKeys.cards.detail(event.card_id), queryKeys.cards.history(event.card_id));
      }
  }
  return keys;
}

/**
 * Owns the WebSocket of the signed-in application and turns hints into TanStack Query
 * invalidations (coalesced: a burst of events refetches each affected query once). Presence is
 * ephemeral and kept outside the query cache.
 */
export function RealtimeProvider({
  children,
  enabled = env.mode !== 'test',
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const presence = useMemo(() => new PresenceStore(), []);
  const client = useMemo(
    () => (enabled ? new RealtimeClient({ url: () => realtimeUrl(env.apiBaseUrl) }) : null),
    [enabled],
  );
  const pending = useRef(new Map<string, readonly unknown[]>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!client) return;
    const queued = pending.current;
    const flush = () => {
      timer.current = null;
      const keys = [...queued.values()];
      queued.clear();
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    };
    const schedule = (keys: readonly (readonly unknown[])[]) => {
      for (const key of keys) queued.set(JSON.stringify(key), key);
      timer.current ??= setTimeout(flush, 150);
    };
    // Access changes are never held back behind the ordinary batching window.
    const scheduleNow = (keys: readonly (readonly unknown[])[]) => {
      schedule(keys);
      if (timer.current !== null) clearTimeout(timer.current);
      flush();
    };
    // Record pages of a database: a burst of edits by others is one read per table per interval.
    const recordQueue = new Map<string, readonly unknown[]>();
    let recordTimer: ReturnType<typeof setTimeout> | null = null;
    let lastRecordFlush = 0;
    const flushRecords = () => {
      recordTimer = null;
      lastRecordFlush = Date.now();
      const keys = [...recordQueue.values()];
      recordQueue.clear();
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    };
    const scheduleRecords = (keys: readonly (readonly unknown[])[]) => {
      for (const key of keys) recordQueue.set(JSON.stringify(key), key);
      if (recordTimer !== null) return;
      const wait = Math.max(150, lastRecordFlush + RECORDS_MIN_INTERVAL_MS - Date.now());
      recordTimer = setTimeout(flushRecords, wait);
    };
    // The viewer lost a database: nothing of its contents may stay in the cache or on screen.
    const dropDatabase = (databaseId: string) => {
      queryClient.removeQueries({
        predicate: (query) => {
          const [root, kind] = query.queryKey;
          return (
            root === 'databases' &&
            (kind === 'records' ||
              kind === 'table' ||
              kind === 'views' ||
              kind === 'history' ||
              kind === 'history-details' ||
              kind === 'record-history') &&
            !query.isActive()
          );
        },
      });
      void queryClient.cancelQueries({ queryKey: ['databases'] });
      scheduleNow([
        queryKeys.databases.detail(databaseId),
        queryKeys.databases.access(databaseId),
        queryKeys.databases.tables(databaseId),
        ...DATABASE_ACCESS_KEYS,
        ['databases', 'records'],
        ['databases', 'table'],
        ['databases', 'views'],
      ]);
    };
    const off = client.on((event) => {
      switch (event.type) {
        case 'board.changed':
          schedule(keysFor(event));
          break;
        case 'database.changed':
          if (event.scope === 'records') scheduleRecords(keysForDatabase(event));
          else schedule(keysForDatabase(event));
          break;
        case 'presence.changed':
          if (event.board_id) presence.set(event.board_id, event.users ?? [], event.editing ?? []);
          break;
        case 'subscribed':
          if (event.board_id) {
            presence.set(
              event.board_id,
              event.presence?.users ?? [],
              event.presence?.editing ?? [],
            );
          }
          break;
        case 'unsubscribed':
          if (event.database_id && event.reason === 'access_revoked') {
            dropDatabase(event.database_id);
          }
          if (event.board_id) {
            presence.clear(event.board_id);
            if (event.reason === 'access_revoked') {
              // Protected data of this board must not linger in the cache: the open board
              // starts over (and answers 404 -> the "no access" state), the rest is dropped.
              const boardId = event.board_id;
              void queryClient.resetQueries({ queryKey: queryKeys.boards.detail(boardId) });
              queryClient.removeQueries({
                predicate: (query) =>
                  JSON.stringify(query.queryKey).includes(boardId) &&
                  JSON.stringify(query.queryKey) !==
                    JSON.stringify(queryKeys.boards.detail(boardId)),
              });
              queryClient.removeQueries({ queryKey: queryKeys.cards.all });
            }
            schedule([
              queryKeys.boards.detail(event.board_id),
              queryKeys.collab.myBoards,
              queryKeys.boards.all,
            ]);
          }
          break;
        case 'access.changed':
          scheduleNow([
            ...DATABASE_ACCESS_KEYS,
            ['collab'],
            queryKeys.workspaces.all,
            queryKeys.boards.all,
            queryKeys.collab.notifications,
            // Access decides which tasks are the viewer's: a lost board's tasks disappear.
            queryKeys.myTasks.summary,
            queryKeys.myTasks.listAll,
            [...queryKeys.calendar.all, { kind: 'me' }],
            // Every aggregated calendar (global, workspace) shows only boards the viewer can see:
            // after an access change they are read again at once, never served from the cache.
            queryKeys.calendar.all,
          ]);
          break;
        case 'collection.changed':
          // A list this person can see changed: refresh exactly that scope (ids only, no data).
          schedule(keysForCollection(event));
          break;
        case 'feedback.changed':
          // A suggestion of this person (or one they moderate) changed: ids only, no content.
          schedule([queryKeys.feedback.all]);
          break;
        case 'notifications.changed':
          schedule([
            queryKeys.collab.unread,
            queryKeys.collab.notifications,
            queryKeys.collab.myInvitations,
          ]);
          break;
        case 'resync':
        case 'reconnected':
          // Anything may have been missed (the socket was down, or the server's bus was
          // rebuilt): refetch what is on screen (inactive queries are only marked stale and load
          // when next used - no reload of every board).
          void queryClient.invalidateQueries();
          break;
        case 'unauthenticated':
          void queryClient.invalidateQueries({ queryKey: queryKeys.viewer });
          break;
        default:
      }
    });
    // Freshness fallback: WebSocket is primary. Only while the connection is NOT healthy does a
    // visible tab re-read what is on screen about every 30 s; a healthy socket costs nothing.
    let fallback: ReturnType<typeof setInterval> | null = null;
    const syncFallback = () => {
      const needed = client.state !== 'open' && client.state !== 'idle';
      if (needed && fallback === null) {
        fallback = setInterval(() => {
          if (document.visibilityState === 'visible') {
            void queryClient.invalidateQueries({ refetchType: 'active' });
          }
        }, FALLBACK_INTERVAL_MS);
      } else if (!needed && fallback !== null) {
        clearInterval(fallback);
        fallback = null;
      }
    };
    const offState = client.onState(syncFallback);
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (shouldRefreshOnVisible(away, client.state)) {
        void queryClient.invalidateQueries({ refetchType: 'active' });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    client.start();
    return () => {
      off();
      offState();
      document.removeEventListener('visibilitychange', onVisibility);
      if (fallback !== null) clearInterval(fallback);
      client.stop();
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      queued.clear();
      if (recordTimer !== null) clearTimeout(recordTimer);
      recordQueue.clear();
    };
  }, [client, presence, queryClient]);

  const value = useMemo(() => ({ client, presence }), [client, presence]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
